import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import { EMPTY_SIGNAL_FILTERS } from './FollowTargetSignalFilters';

const STORAGE_KEY = 'wingr.vps.selectedAgentId';
/** Sentinel for “all agents” overview (Automations). */
export const VPS_ALL_AGENTS = 'all';

const AGENTS_TTL_MS = 12_000;
const AGENTS_POLL_MS = 30_000;

/** Module-level cache so Overview / Automations / Accounts share one list fetch. */
let agentsCache = {
  list: null,
  at: 0,
  inflight: null
};

async function fetchVpsAgentsList({ force = false } = {}) {
  const now = Date.now();
  if (!force && agentsCache.list && (now - agentsCache.at) < AGENTS_TTL_MS) {
    return agentsCache.list;
  }
  if (agentsCache.inflight) return agentsCache.inflight;

  agentsCache.inflight = (async () => {
    const res = await api.get('/admin/va/ai-agents', { params: { compact: 1 } });
    const list = res.data?.agents || [];
    agentsCache = { list, at: Date.now(), inflight: null };
    return list;
  })().catch((err) => {
    agentsCache.inflight = null;
    throw err;
  });

  return agentsCache.inflight;
}

/**
 * Shared agent list + selection for VPS nav pages.
 * @param {{ allowAll?: boolean }} [opts] — when true, `all` is a valid selection (defaults to all).
 */
export function useVpsAgents({ allowAll = false } = {}) {
  const [agents, setAgents] = useState(() => agentsCache.list || []);
  const [selectedId, setSelectedId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) || '';
      if (allowAll && (!stored || stored === VPS_ALL_AGENTS)) return VPS_ALL_AGENTS;
      if (!allowAll && stored === VPS_ALL_AGENTS) return '';
      return stored;
    } catch {
      return allowAll ? VPS_ALL_AGENTS : '';
    }
  });
  const [loading, setLoading] = useState(() => !agentsCache.list);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!silent && !agentsCache.list) setLoading(true);
    try {
      const list = await fetchVpsAgentsList({ force });
      setAgents(list);
      setError(null);
      setSelectedId((prev) => {
        if (allowAll && prev === VPS_ALL_AGENTS) return VPS_ALL_AGENTS;
        if (prev && prev !== VPS_ALL_AGENTS && list.some((a) => String(a._id) === String(prev))) {
          return prev;
        }
        if (allowAll) {
          try { localStorage.setItem(STORAGE_KEY, VPS_ALL_AGENTS); } catch { /* ignore */ }
          return VPS_ALL_AGENTS;
        }
        const first = list[0]?._id ? String(list[0]._id) : '';
        try {
          if (first) localStorage.setItem(STORAGE_KEY, first);
        } catch { /* ignore */ }
        return first;
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load agents');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [allowAll]);

  useEffect(() => {
    load({ silent: Boolean(agentsCache.list) });
    const id = window.setInterval(() => load({ silent: true }), AGENTS_POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const selectAgent = useCallback((id) => {
    const next = String(id || '');
    setSelectedId(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
    } catch { /* ignore */ }
  }, []);

  const selected = agents.find((a) => String(a._id) === String(selectedId)) || null;
  const isAllAgents = allowAll && selectedId === VPS_ALL_AGENTS;

  return {
    agents,
    selected,
    selectedId,
    selectAgent,
    isAllAgents,
    loading,
    error,
    reload: load
  };
}

/**
 * Apply agent config/source fields into form state when selection changes.
 * Returns true if an agent was found and applied.
 */
export function applyAgentFormFields(agent, setters, defaults) {
  if (!agent || !setters) return false;
  const {
    setConfig,
    setSourceType,
    setScrapeTag,
    setFollowModelId,
    setAgeBuckets,
    setIncludeUnknownAge,
    setPlatforms,
    setIncludeUnknownPlatform,
    setSignalFilters
  } = setters;
  if (setConfig) {
    setConfig({ ...(defaults || {}), ...(agent.config || {}) });
  }
  if (setSourceType) {
    setSourceType(agent.targetSource?.type || 'scrape_v2_pool');
  }
  if (setScrapeTag) {
    setScrapeTag(agent.targetSource?.scrapeTag || '');
  }
  if (setFollowModelId) {
    setFollowModelId(agent.followModelId ? String(agent.followModelId) : '');
  }
  if (setAgeBuckets) {
    const buckets = agent.targetSource?.ageBuckets;
    setAgeBuckets(Array.isArray(buckets) ? buckets.map(Number).filter(Number.isFinite) : []);
  }
  if (setIncludeUnknownAge) {
    setIncludeUnknownAge(agent.targetSource?.includeUnknownAge !== false);
  }
  if (setPlatforms) {
    const selected = agent.targetSource?.platforms;
    setPlatforms(Array.isArray(selected) ? selected.map(String) : []);
  }
  if (setIncludeUnknownPlatform) {
    setIncludeUnknownPlatform(agent.targetSource?.includeUnknownPlatform !== false);
  }
  if (setSignalFilters) {
    setSignalFilters({
      ...EMPTY_SIGNAL_FILTERS,
      ...(agent.targetSource?.signalFilters || {})
    });
  }
  return true;
}

/** Ignore stale async responses after agent switch / remount. */
export function useRequestGeneration() {
  const genRef = useRef(0);
  const bump = useCallback(() => {
    genRef.current += 1;
    return genRef.current;
  }, []);
  const isCurrent = useCallback((gen) => gen === genRef.current, []);
  return { bump, isCurrent, genRef };
}

export function VpsAgentPicker({
  agents,
  selectedId,
  onSelect,
  allowAll = false,
  className = '',
  onChanged
}) {
  const [creating, setCreating] = useState(false);
  const [paired, setPaired] = useState(null);
  const selected = agents.find((a) => String(a._id) === String(selectedId)) || null;
  const canRemove = Boolean(selected) && selectedId !== VPS_ALL_AGENTS;

  return (
    <div className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      <label className="inline-flex items-center gap-2 text-sm">
        <span className="text-gray-500 dark:text-gray-400">Agent</span>
        <select
          className="form-select text-sm min-w-[12rem]"
          value={selectedId || ''}
          onChange={(e) => onSelect(e.target.value)}
        >
          {allowAll && (
            <option value={VPS_ALL_AGENTS}>All agents</option>
          )}
          {!agents.length && !allowAll && <option value="">No agents</option>}
          {agents.map((a) => (
            <option key={a._id} value={a._id}>
              {a.name}
              {a.status && a.status !== 'idle' ? ` · ${a.status}` : ''}
            </option>
          ))}
        </select>
      </label>
      <button type="button" className="btn-secondary !py-1.5 !px-3 text-xs" onClick={() => setCreating(true)}>
        New VPS
      </button>
      <button
        type="button"
        className="btn-secondary !py-1.5 !px-3 text-xs text-red-600 dark:text-red-400"
        disabled={!canRemove}
        title={canRemove ? `Remove ${selected.name}` : 'Pick one VPS to remove'}
        onClick={() => removeVpsAgent(selected, onChanged)}
      >
        Remove
      </button>
      {creating && (
        <CreateVpsModal
          onClose={() => setCreating(false)}
          onCreated={async (agent) => {
            setCreating(false);
            setPaired({
              name: agent.name,
              token: agent.desktopAgentToken || ''
            });
            if (onChanged) await onChanged(String(agent._id));
            else onSelect(String(agent._id));
          }}
        />
      )}
      {paired && (
        <PairingTokenModal agent={paired} onClose={() => setPaired(null)} />
      )}
    </div>
  );
}

export async function removeVpsAgent(agent, onChanged) {
  if (!agent?._id) return;
  const ok = window.confirm(
    `Remove VPS “${agent.name}”? Pending jobs are cancelled. The machine stays up, and the X accounts are not deleted.`
  );
  if (!ok) return;
  try {
    await api.delete(`/admin/va/ai-agents/${agent._id}`);
    toast.success(`Removed ${agent.name}`);
    if (onChanged) await onChanged(null);
  } catch (err) {
    toast.error(err.response?.data?.error || 'Failed to remove VPS');
  }
}

function CreateVpsModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/admin/va/ai-agents', { name: trimmed });
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to create VPS');
      toast.success(`Created ${trimmed}`);
      await onCreated(res.data.agent);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to create VPS');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">New VPS</h3>
          <p className="mt-1 text-sm text-gray-500">This adds an agent. Pair the desktop worker on the machine with the token on the next step.</p>
        </div>
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
          <input
            autoFocus
            className="form-input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Warmup VPS"
          />
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-200 dark:border-gray-700">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
}

function PairingTokenModal({ agent, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(agent.token || '');
      toast.success('Token copied');
    } catch (_) {
      toast.error('Could not copy the token');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">Pair {agent.name}</h3>
        <p className="text-sm text-gray-500">
          Put this token in the desktop agent on that machine as <span className="font-mono">WINGR_AGENT_TOKEN</span>, then start the worker. It shows online here within a few seconds.
        </p>
        <input readOnly className="form-input w-full font-mono text-xs" value={agent.token || ''} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={copy}>Copy token</button>
          <button type="button" className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function VpsPageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

/** Wire values: `classic` | `v3` (Humanized 1) | `h2` (Humanized 2). */
export function normalizeFollowRunner(v, fallback = 'classic') {
  const s = String(v || '').toLowerCase();
  if (s === 'classic' || s === 'v3' || s === 'h2') return s;
  return fallback === 'v3' || fallback === 'h2' ? fallback : 'classic';
}

export function isHumanizedFollowRunner(v) {
  const n = normalizeFollowRunner(v);
  return n === 'v3' || n === 'h2';
}

export function labelFollowRunner(v) {
  const n = normalizeFollowRunner(v);
  if (n === 'h2') return 'Humanized 2';
  if (n === 'v3') return 'Humanized 1';
  return 'URL follow';
}

export function formatFollowBuildCounts({ classic = 0, v3 = 0, h2 = 0, none = 0 } = {}) {
  const parts = [
    `${classic} URL follow`,
    `${v3} Humanized 1`,
    `${h2} Humanized 2`
  ];
  if (none > 0) parts.push(`${none} no user-profile`);
  return parts.join(' · ');
}
